import { NGXLogger } from 'ngx-logger';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { AppConstants } from 'src/app/commons/app-constants';
import { UserRegistration } from '../model/user-registration';
import { AppRoutePathConstants } from 'src/app/commons/app-route-path-constants';
import { isIosNative } from 'src/app/commons/utils/platform';
import { LegalService } from 'src/app/services/legal.service';


const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};


/** The mobile verification code settings (Application Settings, Super Admin / Root). */
export interface AdminOtpSettings {
  whatsappEnabled: boolean;
  userDailyCap: number;
  ipDailyCap: number;
  totalDailyCap: number;
  /** The WhatsApp Business sender (CRM) is on. Read only. */
  whatsappSenderReady?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserRegistrationService {
  private readonly logger = inject(NGXLogger);


  private apiUrl = AppConstants.API_URL + "api/v1/userRegistrations/";
  private apiUrlAccount = AppConstants.API_URL + "api/v1/register/account-details";
  private apiUrlCompany = AppConstants.API_URL + "api/v1/register/company-details";



  public userLoaded: EventEmitter<UserRegistration> = new EventEmitter();


  private legalService = inject(LegalService);

  constructor(private http: HttpClient) { }

  getAll(): Observable<UserRegistration[]> {
    return this.http.get<UserRegistration[]>(this.apiUrl);
  }

  get(id: number): Observable<UserRegistration> {
    return this.http.get<UserRegistration>(`${this.apiUrl}/${id}`);
  }

  /* save(userRegistration: UserRegistration): Observable<UserRegistration> {
    console.log(`Save Url ${this.apiUrl}`);
    return this.http.post<UserRegistration>(this.apiUrl, userRegistration);
  } */

  save(userRegistration: UserRegistration, isUserInvited = false, invitationToken: string | null): Observable<UserRegistration> {
    this.logger.debug(`Save Url ${this.apiUrl}`);

    let params = new HttpParams();
    // Add any query parameters you need
    if (isUserInvited) {
      params = params.append('isUserInvited', 'true');
    }
    if (invitationToken) {
      params = params.append('invitationToken', invitationToken);
    }

    return this.http.post<UserRegistration>(this.apiUrl, userRegistration, { params });
  }

  update(id: number, userRegistration: UserRegistration): Observable<UserRegistration> {
    return this.http.put<UserRegistration>(`${this.apiUrl}/${id}`, userRegistration);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}api/v1/${id}`);
  }


  /* getUserRegistrationByUserId(): Observable<UserRegistration> {
    const url = this.apiUrl + "userId";
    console.log(`Url is ${url}`);
    return this.http.get<UserRegistration>(`${url}`);
  } */
  getUserRegistrationByUserId(): Observable<UserRegistration> {
    const url = this.apiUrl + "userId";
    return this.http.get<UserRegistration>(url).pipe(
      tap(data => this.userLoaded.emit(data)) // Use tap operator to trigger the event when the data is loaded
    );
  }

  getAllCountries(): Observable<any> {
    const url = AppConstants.API_URL + "api/v1/countries";
    return this.http.get<any>(`${url}`);
  }

  getAllISDCodes(): Observable<any> {
    const url = AppConstants.API_URL + "api/v1/countries/iso-codes";
    return this.http.get<any>(`${url}`);
  }

  /** channel: SMS (default) or WHATSAPP. */
  sendOTP(phoneNumber: string, isdCode?: string, channel?: 'SMS' | 'WHATSAPP'): Observable<any> {
    let url = AppConstants.API_URL + "api/v1/otp-generate?phoneNumber=" + phoneNumber;
    if (isdCode) {
      url += `&isdCode=${isdCode}`;
    }
    if (channel) {
      url += `&channel=${channel}`;
    }
    this.logger.debug(`Save Url ${url}`);
    return this.http.post<any>(url, null);
  }

  /** channel: the one the code was sent on (SMS or WHATSAPP). */
  validateOTP(phoneNumber: string, otp: number | string, isdCode?: number | string, channel?: 'SMS' | 'WHATSAPP') {
    let url = AppConstants.API_URL +
      `api/v1/otp-validate?phoneNumber=${phoneNumber}&otp=${otp}`;

    // Append isdCode only if it exists
    if (isdCode) {
      url += `&isdCode=${isdCode}`;
    }
    if (channel) {
      url += `&channel=${channel}`;
    }
    this.logger.debug(`Save Url ${url}`);
    return this.http.post<any>(url, null);
  }

  /** May the screen offer "send the code on WhatsApp"? (the switch in Application Settings AND the WhatsApp sender are on) */
  getOtpOptions(): Observable<{ whatsappAvailable: boolean }> {
    return this.http.get<{ whatsappAvailable: boolean }>(AppConstants.API_URL + 'api/v1/otp-options');
  }

  /** How far the last WhatsApp code to this number got: NONE, SENT, DELIVERED or FAILED. Asked once or twice, never on a timer. */
  getOtpStatus(phoneNumber: string, isdCode?: string): Observable<{ status: 'NONE' | 'SENT' | 'DELIVERED' | 'FAILED' }> {
    let url = AppConstants.API_URL + `api/v1/otp-status?phoneNumber=${encodeURIComponent(phoneNumber)}`;
    if (isdCode) url += `&isdCode=${encodeURIComponent(isdCode)}`;
    return this.http.get<{ status: 'NONE' | 'SENT' | 'DELIVERED' | 'FAILED' }>(url);
  }

  /** Super Admin / Root: the WhatsApp code switch and the daily limits. */
  getAdminOtpSettings(): Observable<AdminOtpSettings> {
    return this.http.get<AdminOtpSettings>(AppConstants.API_URL + 'api/v1/admin/otp-settings');
  }

  saveAdminOtpSettings(body: AdminOtpSettings): Observable<AdminOtpSettings> {
    return this.http.put<AdminOtpSettings>(AppConstants.API_URL + 'api/v1/admin/otp-settings', body);
  }

  saveUserAccountDetails(userCombinedDetails: any): Observable<any> {
    this.logger.debug(`Save Url ${this.apiUrlAccount}`);

    return this.http.post<UserRegistration>(this.apiUrlAccount, userCombinedDetails, { withCredentials: true });
  } ///api/v1

  saveCompanyDetails(data: any): Observable<any> {
    this.logger.debug(`Save apiUrlCompany Url ${this.apiUrlCompany}`);
    // Sent only on iOS — lets the backend auto-activate the free-forever plan in place of the
    // paid subscription/payment steps Apple's guidelines block on iOS. Not security-sensitive
    // (see backend CompanyTallysheetController.saveCompany's own comment).
    const headers = isIosNative() ? new HttpHeaders({ 'X-Client-Platform': 'ios' }) : undefined;
    return this.http.post<UserRegistration>(this.apiUrlCompany, data, { withCredentials: true, headers });
  } ///api/v1

  getAccountDetails(): Observable<any> {
    const url = AppConstants.API_URL + "api/v1/account-details";
    return this.http.get<any>(`${url}`);
  }

  getCompanyDetails(): Observable<any> {
    const url = AppConstants.API_URL + "api/v1/company-details";
    return this.http.get<any>(`${url}`);
  }


  updateCompanyDetails(data: any): Observable<any> {
    const url = AppConstants.API_URL + 'api/v1/update/company-details'
    this.logger.debug(`updateCompanyDetails Url ${url}`);
    return this.http.put<UserRegistration>(url, data, { withCredentials: true });
  }
  acceptTerms(termsVersion = "2026-05"): Observable<any> {
    const url = AppConstants.API_URL + "api/v1/compliance/terms";
    // the server records every published document here: tell the app shell so the "legal documents" prompt re-checks and goes away
    return this.http.post<any>(url, { termsVersion }, { withCredentials: true })
      .pipe(tap(() => this.legalService.notifyAcceptancesChanged()));
  }

  completeOnboarding(): Observable<any> {
    const url = AppConstants.API_URL + "api/v1/onboarding/complete";
    return this.http.post<any>(url, {}, { withCredentials: true });
  }


  changePasswordService({ oldPassword, newPassword }): Observable<any> {
    const url = AppConstants.API_URL + "api/v1/password/change-password";
    return this.http.post(url, { oldPassword, newPassword });

  }
}
